/**
 * Format checks for values that arrive in the address bar. A route param is checked before any
 * call is made with it, so a bad link shows a not-found screen instead of a request the server
 * refuses, or one built to reach another path (SEC-20). Encoding still happens in `lib/http`;
 * this is the second line, not the only one.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Job, run, case, review and sheet ids: the API takes each as a UUID. */
export function isUuid(value: string | null | undefined): value is string {
  return typeof value === "string" && UUID.test(value);
}

/** Longest package or tag name accepted from an address. Real ones are far shorter. */
const MAX_NAME = 200;

/**
 * A package or tag name. Tags can carry spaces and a slash (`FV 101/A`), and encoding keeps those
 * inside one segment, so only what no name holds is refused: nothing, a `.` or `..` between
 * slashes (`../auth`), a control character, or something absurdly long.
 */
export function isName(value: string | null | undefined): value is string {
  if (typeof value !== "string" || value.trim() === "" || value.length > MAX_NAME) return false;
  if (value.split("/").some((part) => part === "." || part === "..")) return false;
  // eslint-disable-next-line no-control-regex
  return !/[\u0000-\u001f\u007f]/.test(value);
}

/** A document id: 32 lowercase hex characters, from the API's document list. */
export function isDocumentId(value: string | null | undefined): value is string {
  return typeof value === "string" && /^[0-9a-f]{32}$/.test(value);
}

/** A NeIO Core trace id, as the API accepts it: `<run id>.<tag key>.<segment>` or `uc1.<job id>...`. */
export function isTraceId(value: string | null | undefined): value is string {
  return typeof value === "string" && /^[A-Za-z0-9._:-]{1,64}$/.test(value);
}
